/**
 * Filtrování HTTP chyb, které tečou do Sentry přes SentryErrorHandler (DEV-733).
 *
 * Angular obalí každou neúspěšnou HTTP odpověď do HttpErrorResponse a `@sentry/angular`
 * z ní udělá Error se zprávou `Http failure response for <url>: <status> <text>`.
 * Sentry takové eventy grupuje podle stack framu, kde se `captureException` zavolalo —
 * tedy podle `captureException` / `ZoneImpl.run` / `Object.S`. Výsledek: jeden koš
 * míchá desítky nesouvisejících endpointů a naopak jedna a tatáž chyba se rozpadá
 * do několika issues podle toho, kudy zrovna probublala.
 *
 * Tenhle modul dělá čtyři věci:
 *  1. zahazuje `status: 0` — přerušený request (uživatel odnavigoval, usnul notebook,
 *     vypadla síť). Není to chyba aplikace a nedá se s tím nic dělat,
 *  2. přefingerprintuje HTTP chyby na `status + endpoint`, takže outage je jedno issue
 *     a skutečný bug na konkrétním endpointu se neztratí v koši,
 *  3. zahazuje následky odhlášení (chybějící projectKey, 401), když uživatel není přihlášený (DEV-3143),
 *  4. drží dedup okno — z jednoho fingerprintu pustí max jeden event za 5 minut,
 *     takže rolling restart backendu nevygeneruje stovky eventů.
 */

import type { ErrorEvent as SentryErrorEvent, EventHint } from '@sentry/angular'

const HTTP_FAILURE_PATTERN = /Http failure response for (\S+): (\d+)/

// Zpráva generovaného API klienta, když se volá bez projectKey (po resetu session je null)
const MISSING_PROJECT_KEY_PATTERN = /^Required parameter projectKey was null or undefined when calling /

const DEDUP_WINDOW_MS = 5 * 60 * 1000

// segmenty, které nenesou informaci a jen by tříštily fingerprint
const BACKEND_PREFIXES = ['backend', 'assets-backend', 'rest']

export interface HttpFailure {
    status: number;
    endpoint: string;
}

export interface BeforeSendOptions {
    now?: () => number;
    windowMs?: number;
    // Vrací, zda je přihlášený uživatel. Výchozí = ano, takže bez předání se nic navíc nezahazuje.
    isLoggedIn?: () => boolean;
}

/**
 * Zbaví URL originu, prefixů backendu, klíče projektu a konkrétních id,
 * ať `.../ca_zos/parcels/123` a `.../ca_demo/parcels/456` skončí ve stejné skupině.
 */
export const normalizeEndpoint = (url: string): string => {
    const withoutOrigin = url.replace(/^https?:\/\/[^/]+/, '')
    const withoutQuery = withoutOrigin.split('?')[0]

    return withoutQuery
        .split('/')
        .filter(segment => segment.length > 0)
        .filter(segment => !BACKEND_PREFIXES.includes(segment))
        .filter(segment => !segment.startsWith('ca_'))
        .map(segment => /^\d+$/.test(segment) ? ':id' : segment)
        .join('/')
}

export const parseHttpFailure = (message: string): HttpFailure | null => {
    const match = message?.match(HTTP_FAILURE_PATTERN)

    if (!match) {
        return null
    }

    return {
        status: Number(match[2]),
        endpoint: normalizeEndpoint(match[1])
    }
}

const readMessage = (event: SentryErrorEvent): string =>
    event?.exception?.values?.[0]?.value || event?.message || ''

const statusCategory = (status: number): string => {
    if (status >= 500) {
        return 'server_5xx'
    }

    return status >= 400 ? 'client_4xx' : 'http_other'
}

/**
 * Vyrobí `beforeSend` hook pro `Sentry.init`. Stav dedup okna si drží instance,
 * proto se v testech vytváří vždy nová (a dá se podstrčit vlastní hodinky).
 */
export const createBeforeSend = (
    options: BeforeSendOptions = {}
): ((event: SentryErrorEvent, hint?: EventHint) => SentryErrorEvent | null) => {
    const now = options.now || ((): number => Date.now())
    const windowMs = options.windowMs === undefined ? DEDUP_WINDOW_MS : options.windowMs
    const isLoggedIn = options.isLoggedIn || ((): boolean => true)
    const lastSentAt = new Map<string, number>()

    return (event: SentryErrorEvent): SentryErrorEvent | null => {
        const message = readMessage(event)
        const failure = parseHttpFailure(message)

        // DEV-3143: po vypršení session jsou tyhle chyby jen následek odhlášení, ne chyba aplikace.
        // Zahazuje se výhradně chybějící projectKey a 401 — ostatní chyby projdou i bez přihlášení.
        if (!isLoggedIn() && (MISSING_PROJECT_KEY_PATTERN.test(message) || failure?.status === 401)) {
            return null
        }

        if (!failure) {
            return event
        }

        // přerušený / nedoručený request — uživatel odnavigoval nebo vypadla síť
        if (failure.status === 0) {
            return null
        }

        const groupKey = `${ failure.status }:${ failure.endpoint }`
        const previous = lastSentAt.get(groupKey)

        if (previous !== undefined && now() - previous < windowMs) {
            return null
        }

        lastSentAt.set(groupKey, now())

        return {
            ...event,
            fingerprint: ['http-failure', String(failure.status), failure.endpoint],
            tags: {
                ...event.tags,
                'http.status': String(failure.status),
                'error.category': statusCategory(failure.status)
            }
        }
    }
}
